import { trackPendoEvent } from 'services/PendoService';
import type { HandPace } from 'utils/handPace';

/**
 * EN-11182: the client-side guardrail events for the pace-of-play rollout
 * (EN-10974). Pace alone only says the gap shrank; these say whether players
 * pay for it. Each is a Pendo event as the identified player -- a guest sends
 * nothing -- carrying the table's pace so phases can be compared:
 *
 *   player_action_not_applied  an action that did not land cleanly: `lost`
 *                              (the clock acted for the player), `abandoned`
 *                              (delivery gave up and the row came back), or
 *                              `settled_after_retry`. The engine logs none of
 *                              the not-your-turn / too-late refusals, so this
 *                              is the action-rejected guardrail.
 *   player_action_fast         an action clicked within FAST_ACTION_MS of the
 *                              turn reaching this client -- one misclick proxy:
 *                              controls that appear sooner are clicked before
 *                              they are read.
 *   free_fold_prompt_answered  the "it's free to check" prompt after Fold, and
 *                              what the player chose. `check` is a fold they
 *                              did not mean: the second misclick proxy.
 *   connection_lost            the connection left `connected`, and why; the
 *                              disconnect/reconnect guardrail.
 *
 * Bounded per session (MAX_PER_EVENT_PER_SESSION of each), and each carries a
 * dedupe key, so a table in trouble cannot flood Pendo. No PII: table and hand
 * facts only; the visitor is Pendo's identified player.
 */

export const FAST_ACTION_MS = 300;
export const MAX_PER_EVENT_PER_SESSION = 30;

/** Which pace a cash table ran at, from the handPace its frames carry. */
export type PaceVariant = 'default' | 'trimmed' | 'chain' | 'unknown';

export const paceVariant = (pace: HandPace | undefined | null): PaceVariant => {
  if (!pace) return 'unknown';
  if (pace.chainSteps) return 'chain';
  return pace.isDefault ? 'default' : 'trimmed';
};

const sentByEvent = new Map<string, number>();
/**
 * Dedupe keys already counted toward the cap. trackPendoEvent answers true for a
 * key it already took, so without this a repeat would use up the cap and hide
 * later, different events. At most MAX_PER_EVENT_PER_SESSION per event.
 */
const countedKeys = new Set<string>();

type Properties = Record<string, string | number | boolean>;

const track = (playerGUID: string, eventName: string, dedupe: string, properties: Properties) => {
  if (!playerGUID) return false;
  const dedupeKey = `${playerGUID}:${eventName}:${dedupe}`;
  // Already sent: Pendo would drop it as a duplicate, and it is not a new report.
  if (countedKeys.has(dedupeKey)) return true;
  const sent = sentByEvent.get(eventName) ?? 0;
  if (sent >= MAX_PER_EVENT_PER_SESSION) return false;
  const accepted = trackPendoEvent({ playerGUID, eventName, properties, dedupeKey });
  if (accepted) {
    countedKeys.add(dedupeKey);
    sentByEvent.set(eventName, sent + 1);
  }
  return accepted;
};

/** The player and table an event is about. */
export interface GuardrailTable {
  playerGUID: string;
  gameID: string | number;
  /** texas | omaha */
  game?: string;
  /** s (cash) | m (MTT) | t (sit & go) */
  tabletype?: string;
  pace?: HandPace | null;
}

interface TableFacts extends GuardrailTable {
  /** The turn the action was made on (turnIdentity), for the dedupe key. */
  turn: string;
}

const tableProperties = ({ gameID, game, tabletype, pace }: TableFacts): Properties => ({
  gameID: String(gameID),
  game: game ?? '',
  tabletype: tabletype ?? '',
  pace: paceVariant(pace)
});

export type ActionNotApplied = 'lost' | 'abandoned' | 'settled_after_retry';

export const reportActionNotApplied = (
  facts: TableFacts & {
    outcome: ActionNotApplied;
    action: string;
    attempts: number;
    heldForMs: number;
  }
) =>
  track(facts.playerGUID, 'player_action_not_applied', `${facts.gameID}:${facts.turn}`, {
    ...tableProperties(facts),
    outcome: facts.outcome,
    action: facts.action,
    attempts: facts.attempts,
    heldForMs: Math.round(facts.heldForMs)
  });

/** Reports only an action within FAST_ACTION_MS of the turn reaching the client. */
export const reportActionTiming = (
  facts: TableFacts & { action: string; msSinceTurn: number | null }
) => {
  const { msSinceTurn } = facts;
  if (msSinceTurn === null || msSinceTurn < 0 || msSinceTurn >= FAST_ACTION_MS) return false;
  return track(facts.playerGUID, 'player_action_fast', `${facts.gameID}:${facts.turn}`, {
    ...tableProperties(facts),
    action: facts.action,
    msSinceTurn: Math.round(msSinceTurn)
  });
};

export type FreeFoldAnswer = 'check' | 'fold' | 'dismissed';

export const reportFreeFoldPrompt = (facts: TableFacts & { answer: FreeFoldAnswer }) =>
  // The answer is in the key: a dismissed prompt can be opened again on the same
  // turn, and the Check that follows is the misclick this event measures.
  track(
    facts.playerGUID,
    'free_fold_prompt_answered',
    `${facts.gameID}:${facts.turn}:${facts.answer}`,
    {
      ...tableProperties(facts),
      answer: facts.answer
    }
  );

/**
 * Why the connection left `connected`: the reason ConnectionManager.reconnect()
 * was given, or `transport` when a socket dropped on its own.
 */
export const reportConnectionLost = (playerGUID: string, reason: string, occurrence: number) =>
  track(playerGUID, 'connection_lost', String(occurrence), { reason });

/** Tests only. */
export const resetPaceGuardrails = () => {
  sentByEvent.clear();
  countedKeys.clear();
};
